/*
 * Copyright (c) 2022. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import { Badge, Grid, Tooltip, type TooltipProps, Typography, tooltipClasses } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  type ComponentId,
  format,
  formatDate,
  type SalesforceObject,
  type SFBaseObjectRecord,
} from '@poweralpha/common';
import { useMutation } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { isEqual, sum } from 'lodash';
import { useCallback, useMemo } from 'react';
import { getbnCRMObjectData } from '../../api/bncrm';
import { toast } from '../../paToast';
import { salesforceColor } from '../../styles';
import { formatDollar, untransformSfKey, WHITESPACE } from '../../utils';
import { LoadingIconButton } from '../buttons';
import { Emoji } from '../Emoji';
import { SalesforceIcon } from '../salesforce/SalesforceLinkIcon';
import type { BaseTextFieldProps } from './BaseTextField';
import FormAutocomplete from './FormAutocomplete';
import { FormDateField, type FormDateFieldProps } from './FormDateField';
import FormSwitch from './FormSwitch';
import FormTextField from './FormTextField';
import { useFieldContext } from './formContext';

interface CustomTooltipProps extends TooltipProps {
  warning: boolean;
}

const CustomTooltip = styled(
  ({ className, warning, ...props }: CustomTooltipProps) => (
    <Tooltip {...props} classes={{ popper: `${className} warning` }} />
  ),
  { shouldForwardProp: (prop) => prop !== 'warning' },
)(({ theme, warning }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: warning ? theme.palette.warning.main : salesforceColor,
    color: warning ? theme.palette.common.black : undefined,
    fontSize: theme.typography.pxToRem(12),
  },
}));

function formatVal(
  v: unknown,
  fieldType: 'string' | 'date' | 'checkbox' | 'autocomplete',
  dollarValue: boolean | undefined,
) {
  if (v === null || v === undefined || v === '') return 'blank';
  if (typeof v === 'object') {
    return `"${(v as ComponentId).id}"`;
  }
  if (typeof v === 'boolean') return `${v}`;
  if (fieldType === 'date') {
    return formatDate(v as string, 'YYYY-MM-DD');
  }
  const parsedNumber = Number.parseFloat(v as string);
  if (!Number.isNaN(parsedNumber)) {
    return dollarValue
      ? formatDollar(parsedNumber, 1)
      : format(parsedNumber, undefined, 1, null, false);
  }
  return `"${v}"`;
}

interface BaseSalesforceFieldProps<SFRecord extends ComponentId | SFBaseObjectRecord> {
  sfRecord: SFRecord | null | undefined;
  object: SalesforceObject;
  label?: string;
  sfName?: keyof SFRecord & string;
  loading?: boolean;
  customSfRecordName?: string | Array<string>;
  multiple?: boolean;
  disabled?: boolean;
  disableUpdate?: boolean;
}

type SalesforceDefaultFieldProps<SFRecord extends ComponentId | SFBaseObjectRecord> =
  BaseSalesforceFieldProps<SFRecord> & {
    fieldType?: 'string';
  } & Pick<BaseTextFieldProps, 'numberFormat' | 'rightUnit'>;

type SalesforceStringFieldProps<SFRecord extends ComponentId | SFBaseObjectRecord> =
  BaseSalesforceFieldProps<SFRecord> & {
    fieldType: 'string';
  } & Pick<BaseTextFieldProps, 'numberFormat' | 'rightUnit'>;

type SalesforceDateFieldProps<SFRecord extends ComponentId | SFBaseObjectRecord> =
  BaseSalesforceFieldProps<SFRecord> &
    FormDateFieldProps & {
      fieldType: 'date';
    };

type SalesforceCheckboxFieldProps<SFRecord extends ComponentId | SFBaseObjectRecord> =
  BaseSalesforceFieldProps<SFRecord> & {
    fieldType: 'checkbox';
  };

type SalesforceAutocompleteFieldProps<
  SFRecord extends ComponentId | SFBaseObjectRecord,
  Option,
> = BaseSalesforceFieldProps<SFRecord> & {
  fieldType: 'autocomplete';
  options: Array<Option> | readonly Option[];
  getOptionLabel?: (o: Option) => string;
  disableClearable?: boolean;
};

export type SalesforceFieldProps<SFRecord extends ComponentId | SFBaseObjectRecord, Option> =
  | SalesforceDefaultFieldProps<SFRecord>
  | SalesforceStringFieldProps<SFRecord>
  | SalesforceDateFieldProps<SFRecord>
  | SalesforceCheckboxFieldProps<SFRecord>
  | SalesforceAutocompleteFieldProps<SFRecord, Option>;

function SalesforceField<SFRecord extends ComponentId | SFBaseObjectRecord, Option>(
  props: SalesforceFieldProps<SFRecord, Option>,
) {
  const { sfName, object, customSfRecordName } = props;
  const field = useFieldContext();
  const value = field.state.value;
  const { name } = field;
  const terminalName = name.split('.').at(-1) ?? '';
  const recordId =
    props.sfRecord && 'Id' in props.sfRecord ? props.sfRecord.Id : props.sfRecord?.id;

  const sfRecordName = useMemo(() => {
    if (customSfRecordName) {
      return Array.isArray(customSfRecordName) ? customSfRecordName.join(', ') : customSfRecordName;
    }

    return untransformSfKey(sfName ?? terminalName);
  }, [customSfRecordName, sfName, terminalName]);

  const { data: fetchedSfValue, mutateAsync: getSfValue } = useMutation<unknown>({
    mutationFn: () =>
      getbnCRMObjectData<Record<keyof SFRecord, unknown>, string>(object, {
        selection: sfRecordName,
        limit: 1,
        id: recordId,
      }).then((data) => {
        if (data === '') {
          toast.error('Record not found');
          return undefined;
        }

        if (Array.isArray(customSfRecordName)) {
          return sum(Object.values(data));
        }

        return Object.values(data)[0];
      }),
  });

  const sfValue = useMemo(
    () => fetchedSfValue ?? props.sfRecord?.[sfName ?? (terminalName as keyof SFRecord)] ?? null,
    [fetchedSfValue, sfName, props.sfRecord, terminalName],
  );

  const handleButtonClick = useCallback(() => {
    getSfValue().then((fetchedSfValue) => {
      let newVal = fetchedSfValue;
      if (newVal === null || newVal === undefined) {
        return;
      }

      if (typeof newVal === 'number') {
        newVal = newVal.toString();
      } else if (props.fieldType === 'date') {
        newVal = dayjs(newVal as string)
          .utc(false)
          .toISOString();
      }

      field.handleChange(props.multiple ? [newVal] : newVal);
    });
  }, [props.multiple, getSfValue, props.fieldType, field]);

  const equalToSfValue = useMemo(() => {
    const sfVal = sfValue as unknown as number | string | boolean | undefined;
    if (sfVal === null || sfVal === undefined) return true;
    if (typeof sfVal === 'object' && 'id' in sfVal)
      return isEqual((value as unknown as ComponentId).id, (sfVal as ComponentId).id);
    if (typeof sfVal === 'boolean') return isEqual(sfVal, value);
    if (props.fieldType === 'date') return isEqual(sfVal, formatDate(value as Date, 'YYYY-MM-DD'));
    return isEqual(sfVal.toString(), value);
  }, [sfValue, props.fieldType, value]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: typing makes this difficult
  const tooltip = useMemo(() => {
    if (equalToSfValue) {
      return (
        <Typography
          align="center"
          sx={{
            fontSize: 14,
          }}
        >
          <Emoji symbol="✅" fontSize={14} />
          This field is up to date with bnCRM
        </Typography>
      );
    }

    let dollarValue: boolean | undefined;
    if (props.fieldType === 'string') {
      dollarValue =
        typeof props.numberFormat === 'object'
          ? props.numberFormat.dollar
          : props.numberFormat === 'dollar';
    }
    const formattedVal = formatVal(value, props.fieldType ?? 'string', dollarValue);
    const formattedOldVal = formatVal(sfValue, props.fieldType ?? 'string', dollarValue);

    return (
      <Typography
        align="center"
        sx={{
          fontSize: 14,
        }}
      >
        {props.label}in bnCRM (
        {formattedOldVal === 'blank' ? <i>{formattedOldVal}</i> : formattedOldVal}) does not match
        {WHITESPACE}
        {formattedVal === 'blank' ? <i>{formattedVal}</i> : formattedVal}
        <br />
        <b>Click to fill with the value in bnCRM</b>
      </Typography>
    );
  }, [value, sfValue, props.fieldType, props.fieldType, props.label, equalToSfValue]);

  return useMemo(
    () => (
      <Grid
        container
        sx={{
          alignItems: 'center',
          columnGap: 1,
        }}
      >
        {(!props.fieldType || props.fieldType === 'string') && (
          <Grid size="grow">
            <FormTextField
              label={props.label}
              numberFormat={props.numberFormat}
              rightUnit={props.rightUnit}
              size="xs"
              disabled={props.disabled}
            />
          </Grid>
        )}
        {props.fieldType === 'date' && (
          <Grid size="grow">
            <FormDateField
              label={props.label}
              mode={props.mode}
              disablePast={props.disablePast}
              disableFuture={props.disableFuture}
              callbackBeforeSet={props.callbackBeforeSet}
              minDateTime={props.minDateTime}
              maxDateTime={props.maxDateTime}
              size="xs"
              disabled={props.disabled}
            />
          </Grid>
        )}
        {props.fieldType === 'checkbox' && (
          <Grid size="grow">
            <FormSwitch label={props.label} disabled={props.disabled} />
          </Grid>
        )}
        {props.fieldType === 'autocomplete' && (
          <Grid size="grow">
            <FormAutocomplete
              freeSolo={false}
              options={props.options}
              getOptionLabel={props.getOptionLabel}
              label={props.label}
              loading={props.loading}
              multiple={props.multiple}
              size="xs"
              disabled={props.disabled}
              disableClearable={props.disableClearable}
            />
          </Grid>
        )}
        <Grid hidden={!props.sfRecord}>
          <LoadingIconButton
            loading={props.loading ?? false}
            onClick={handleButtonClick}
            disabled={props.disableUpdate || sfValue === null}
            data-testid={`update-${terminalName}`}
            sx={{ height: 25, width: 25 }}
          >
            <CustomTooltip title={tooltip} warning={!equalToSfValue}>
              <Badge variant="dot" color="warning" invisible={equalToSfValue}>
                <SalesforceIcon height={25} disabled={props.disableUpdate || sfValue === null} />
              </Badge>
            </CustomTooltip>
          </LoadingIconButton>
        </Grid>
      </Grid>
    ),
    [props, tooltip, equalToSfValue, sfValue, handleButtonClick, terminalName],
  );
}

export default SalesforceField;
